import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Calendar, Package, Banknote, ClipboardList, TrendingUp, Star, Clock, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { statsAPI } from "../api";
import "./Stats.css";

const RestaurantStats = () => {
  const { isRestaurant } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Période par défaut : 6 derniers mois
  const getDefaultStartDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 6);
    return date.toISOString().split('T')[0];
  };
  
  const getDefaultEndDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 90); // Inclure les 90 prochains jours pour les livraisons futures
    return date.toISOString().split('T')[0];
  };
  
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        start_date: startDate,
        end_date: endDate,
      };
      const response = await statsAPI.getRestaurantStats(params);
      setStats(response.data);
    } catch (err) {
      setError("Erreur lors du chargement des statistiques");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (isRestaurant) {
      loadStats();
    }
  }, [isRestaurant, loadStats]);
  
  const handlePeriodChange = () => {
    loadStats();
  };
  
  const resetPeriod = () => {
    setStartDate(getDefaultStartDate());
    setEndDate(getDefaultEndDate());
  };

  if (!isRestaurant) {
    return (
      <div className="container main-content">
        <div className="alert alert-error">
          Vous devez être restaurateur pour voir cette page.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container main-content">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container main-content">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const statusLabels = {
    pending: "En attente",
    confirmed: "Confirmée",
    preparing: "En préparation",
    ready: "Prête",
    shipped: "Expédiée",
    delivered: "Livrée",
    cancelled: "Annulée",
  };

  const statusColors = {
    pending: "var(--warning)",
    confirmed: "var(--info)",
    preparing: "var(--primary)",
    ready: "var(--success)",
    shipped: "var(--info)",
    delivered: "var(--success)",
    cancelled: "var(--error)",
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="container main-content">
      <div className="page-header">
        <h1>Mes Statistiques</h1>
        <p>Vue d'ensemble de vos commandes et dépenses</p>
      </div>

      {/* Sélecteur de période */}
      <div className="card" style={{ marginBottom: "2rem" }}>
        <h3 className="icon-title" style={{ marginBottom: "1rem" }}><Calendar size={18} strokeWidth={2.25} /> Sélectionner la période</h3>
        <div className="stats-period-picker">
          <div className="form-group">
            <label htmlFor="start_date" className="form-label">
              Date de début
            </label>
            <input
              type="date"
              id="start_date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              onBlur={handlePeriodChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="end_date" className="form-label">
              Date de fin
            </label>
            <input
              type="date"
              id="end_date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              onBlur={handlePeriodChange}
            />
          </div>
          <button
            onClick={resetPeriod}
            className="btn btn-outline btn-sm"
            style={{ height: "fit-content" }}
          >
            Réinitialiser
          </button>
        </div>
      </div>

      <div className="stats-grid">
        {/* Commandes */}
        <div className="stat-card card">
          <h3 className="icon-title"><Package size={18} strokeWidth={2.25} /> Commandes</h3>
          <div className="stat-value">{stats.orders.total}</div>
          <div className="stat-label">Commandes totales</div>
          <div className="stat-detail">
            <span className="stat-label">En cours</span>
            <span className="stat-number">{stats.orders.pending_count}</span>
          </div>
        </div>

        {/* Dépenses */}
        <div className="stat-card card">
          <h3 className="icon-title"><Banknote size={18} strokeWidth={2.25} /> Dépenses totales</h3>
          <div className="stat-value-large">
            {stats.orders.total_spent.toLocaleString("fr-FR")} F CFA
          </div>
        </div>

        {/* Commandes par statut */}
        <div className="stat-card card">
          <h3 className="icon-title"><ClipboardList size={18} strokeWidth={2.25} /> Commandes par statut</h3>
          <div className="status-list">
            {Object.entries(stats.orders.orders_by_status).map(([status, count]) => (
              <div key={status} className="status-item">
                <span
                  className="status-badge"
                  style={{ backgroundColor: statusColors[status] || "var(--text-secondary)" }}
                ></span>
                <span className="status-label">{statusLabels[status] || status}</span>
                <span className="status-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline des commandes */}
      <div className="card" style={{ marginTop: "2rem" }}>
        <h3 className="icon-title"><Calendar size={18} strokeWidth={2.25} /> Timeline des commandes</h3>
        <p style={{ fontSize: "0.9rem", color: "var(--text-secondary)", marginBottom: "1rem" }}>
          Période : {new Date(startDate).toLocaleDateString("fr-FR")} - {new Date(endDate).toLocaleDateString("fr-FR")}
        </p>
        <div className="timeline">
          {stats.timeline && stats.timeline.length > 0 ? (
            stats.timeline.map((item, index) => {
              const deliveryDate = item.delivery_date ? new Date(item.delivery_date) : null;
              const isFuture = deliveryDate && deliveryDate > new Date();
              const orderDate = new Date(item.date);
              const isPastOrder = orderDate < new Date();
              
              return (
                <div key={index} className="timeline-item">
                  <div className="timeline-marker" style={{ backgroundColor: statusColors[item.status] || "var(--text-secondary)" }}></div>
                  <div className="timeline-content">
                    <div className="timeline-header">
                      <span className="timeline-date">
                        {isFuture && deliveryDate ? (
                          <span className="icon-inline" style={{ color: "var(--primary)", fontWeight: 600 }}>
                            <Calendar size={14} strokeWidth={2.25} /> {formatDate(item.delivery_date)}
                          </span>
                        ) : (
                          formatDate(item.date)
                        )}
                      </span>
                      <span
                        className="timeline-status"
                        style={{ color: statusColors[item.status] || "var(--text-secondary)" }}
                      >
                        {statusLabels[item.status] || item.status}
                      </span>
                    </div>
                    <div className="timeline-body">
                      <div className="timeline-product">
                        <strong>{item.product_name}</strong>
                        <span>Quantité: {item.quantity}</span>
                      </div>
                      <div className="timeline-details">
                        <span>Fournisseur: {item.seller_name}</span>
                        <span className="timeline-price">
                          {item.total_price.toLocaleString("fr-FR")} F CFA
                        </span>
                      </div>
                      {item.delivery_date && (
                        <div className={`timeline-delivery ${isFuture ? 'timeline-delivery-future' : ''}`}>
                          {isFuture ? (
                            <>
                              <Package size={14} strokeWidth={2.25} style={{verticalAlign: "-2px"}} /> <strong>Livraison prévue:</strong> {formatDate(item.delivery_date)}
                              {deliveryDate && (
                                <span className="timeline-future-badge">
                                  {Math.ceil((deliveryDate - new Date()) / (1000 * 60 * 60 * 24))} jour{Math.ceil((deliveryDate - new Date()) / (1000 * 60 * 60 * 24)) > 1 ? 's' : ''}
                                </span>
                              )}
                            </>
                          ) : (
                            <><Package size={14} strokeWidth={2.25} style={{verticalAlign: "-2px"}} /> Livraison prévue: {formatDate(item.delivery_date)}</>
                          )}
                        </div>
                      )}
                      {!item.delivery_date && isPastOrder && (
                        <div className="timeline-delivery icon-inline">
                          <Calendar size={14} strokeWidth={2.25} /> Commande passée le {formatDate(item.date)}
                        </div>
                      )}
                    </div>
                    <Link to={`/orders/${item.id}`} className="timeline-link icon-inline">
                      Voir les détails <ArrowRight size={13} strokeWidth={2.25} />
                    </Link>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="empty-timeline">
              <p>Aucune commande récente ou future</p>
            </div>
          )}
        </div>
      </div>

      {/* Dépenses par mois */}
      <div className="card" style={{ marginTop: "2rem" }} data-tour="stats-chart">
        <h3 className="icon-title"><TrendingUp size={18} strokeWidth={2.25} /> Dépenses par mois</h3>
        <p style={{ fontSize: "0.9rem", color: "var(--text-secondary)", marginBottom: "1rem" }}>
          Période : {new Date(startDate).toLocaleDateString("fr-FR")} - {new Date(endDate).toLocaleDateString("fr-FR")}
        </p>
        <div className="chart-container">
          <div className="line-chart">
            <svg className="line-chart-svg" viewBox="0 0 800 300" preserveAspectRatio="none">
              {stats.spending_by_month.length > 0 && (() => {
                const maxSpending = Math.max(...stats.spending_by_month.map(s => s.spending), 1);
                const minSpending = Math.min(...stats.spending_by_month.map(s => s.spending), 0);
                const range = maxSpending - minSpending || 1;
                const width = 800;
                const height = 300;
                const padding = 40;
                const chartWidth = width - 2 * padding;
                const chartHeight = height - 2 * padding;
                const stepX = chartWidth / Math.max(stats.spending_by_month.length - 1, 1);
                
                // Créer les points
                const points = stats.spending_by_month.map((item, index) => {
                  const x = padding + index * stepX;
                  const y = padding + chartHeight - ((item.spending - minSpending) / range) * chartHeight;
                  return { x, y, value: item.spending, month: item.month };
                });
                
                // Créer le chemin de la ligne
                const pathData = points.map((point, index) => 
                  `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
                ).join(' ');
                
                // Créer la zone sous la courbe
                const areaPath = `${pathData} L ${points[points.length - 1].x} ${height - padding} L ${points[0].x} ${height - padding} Z`;
                
                return (
                  <>
                    {/* Zone sous la courbe */}
                    <path
                      d={areaPath}
                      fill="url(#gradient)"
                      opacity="0.3"
                    />
                    {/* Ligne */}
                    <path
                      d={pathData}
                      fill="none"
                      stroke="var(--primary)"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {/* Points */}
                    {points.map((point, index) => (
                      <g key={index}>
                        <circle
                          cx={point.x}
                          cy={point.y}
                          r="5"
                          fill="var(--primary)"
                          stroke="white"
                          strokeWidth="2"
                        />
                        <title>{point.month}: {point.value.toLocaleString("fr-FR")} F CFA</title>
                      </g>
                    ))}
                    {/* Définir le gradient */}
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.5" />
                        <stop offset="100%" stopColor="var(--primary)" stopOpacity="0.1" />
                      </linearGradient>
                    </defs>
                  </>
                );
              })()}
            </svg>
            {/* Labels des mois */}
            <div className="line-chart-labels">
              {stats.spending_by_month.map((item, index) => (
                <div key={index} className="line-chart-label">
                  {item.month}
                </div>
              ))}
            </div>
            {/* Légende des valeurs */}
            {stats.spending_by_month.length > 0 && (() => {
              const maxSpending = Math.max(...stats.spending_by_month.map(s => s.spending), 1);
              const minSpending = Math.min(...stats.spending_by_month.map(s => s.spending), 0);
              const range = maxSpending - minSpending || 1;
              const height = 300;
              const padding = 40;
              const chartHeight = height - 2 * padding;
              const stepX = (800 - 2 * padding) / Math.max(stats.spending_by_month.length - 1, 1);
              
              return (
                <div className="line-chart-values">
                  {stats.spending_by_month.map((item, index) => {
                    const x = padding + index * stepX;
                    const y = padding + chartHeight - ((item.spending - minSpending) / range) * chartHeight;
                    
                    return (
                      <div
                        key={index}
                        className="line-chart-value"
                        style={{
                          left: `${(x / 800) * 100}%`,
                          top: `${(y / 300) * 100}%`,
                        }}
                      >
                        {item.spending > 0 && (
                          <span>{item.spending.toLocaleString("fr-FR")} F CFA</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Fournisseurs préférés */}
      {stats.favorite_suppliers && stats.favorite_suppliers.length > 0 && (
        <div className="card" style={{ marginTop: "2rem" }}>
          <h3 className="icon-title"><Star size={18} strokeWidth={2.25} /> Fournisseurs préférés</h3>
          <div className="suppliers-list">
            {stats.favorite_suppliers.map((supplier, index) => (
              <div key={index} className="supplier-item">
                <div className="supplier-rank">#{index + 1}</div>
                <div className="supplier-info">
                  <div className="supplier-name">{supplier.name}</div>
                  <div className="supplier-details">
                    <span>{supplier.order_count} commande{supplier.order_count > 1 ? "s" : ""}</span>
                    <span>{supplier.total_spent.toLocaleString("fr-FR")} F CFA</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Commandes en cours */}
      {stats.pending_orders && stats.pending_orders.length > 0 && (
        <div className="card" style={{ marginTop: "2rem" }}>
          <h3 className="icon-title"><Clock size={18} strokeWidth={2.25} /> Commandes en cours</h3>
          <div className="pending-orders-list">
            {stats.pending_orders.map((order) => (
              <Link
                key={order.id}
                to={`/orders/${order.id}`}
                className="pending-order-item"
              >
                <div className="pending-order-info">
                  <div className="pending-order-product">{order.product_name}</div>
                  <div className="pending-order-details">
                    <span>Quantité: {order.quantity}</span>
                    <span
                      className="pending-order-status"
                      style={{ color: statusColors[order.status] || "var(--text-secondary)" }}
                    >
                      {statusLabels[order.status] || order.status}
                    </span>
                  </div>
                </div>
                <div className="pending-order-price">
                  {order.total_price.toLocaleString("fr-FR")} F CFA
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantStats;

